import { useState, useCallback, type ChangeEvent } from "react";
import { buildWhatsAppLink } from "@/lib/config";
import { trackCta } from "@/lib/tracking";

interface Step {
  id: string;
  label: string;
  type: "text" | "select" | "number";
  placeholder?: string;
  options?: string[];
  hint?: string;
}

const steps: Step[] = [
  { id: "nome", label: "Qual seu nome?", type: "text", placeholder: "Seu nome completo" },
  {
    id: "email",
    label: "Qual seu e-mail?",
    type: "text",
    placeholder: "seu@email.com",
    hint: "Usaremos seu e-mail para vincular a BM à sua conta.",
  },
  { id: "nicho", label: "Qual seu nicho?", type: "text", placeholder: "Ex: moda, finanças, saúde, infoproduto..." },
  {
    id: "investimento",
    label: "Quanto investe em tráfego pago por mês?",
    type: "select",
    options: ["Ainda não invisto", "Até R$ 5.000", "R$ 5.000 a R$ 20.000", "R$ 20.000 a R$ 50.000", "R$ 50.000 a R$ 100.000", "Acima de R$ 100.000"],
  },
  { id: "cpa", label: "Qual seu custo atual por aquisição (CPA)?", type: "text", placeholder: "Ex: R$ 15, R$ 50, R$ 120..." },
];

const inputBase =
  "w-full rounded-xl border border-background-300/50 bg-background-50 px-5 py-4 text-base text-foreground-950 placeholder:text-foreground-500 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20";

export default function BioForm({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState(0);
  const [data, setData] = useState<Record<string, string>>({});
  const [isOpen, setIsOpen] = useState(true);

  const step = steps[current];
  const value = data[step.id] || "";
  const canNext = value.trim().length > 0;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setData((prev) => ({ ...prev, [step.id]: e.target.value }));
    },
    [step.id]
  );

  const handleNext = () => {
    if (!canNext) return;
    if (current < steps.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrev = () => {
    if (current > 0) setCurrent((c) => c - 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleNext();
  };

  const handleSubmit = () => {
    const msg = `Olá! Quero minha BM para rodar no Kwai Ads.\n\nNome: ${data.nome || ""}\nE-mail: ${data.email || ""}\nNicho: ${data.nicho || ""}\nInvestimento mensal: ${data.investimento || ""}\nCPA atual: ${data.cpa || ""}`;
    trackCta("bio_form_whatsapp");
    window.open(buildWhatsAppLink(msg), "_blank", "noopener,noreferrer");
    setIsOpen(false);
    onClose();
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  const progress = ((current + 1) / steps.length) * 100;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-background-300/40 bg-background-100 p-6 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-primary-400 tracking-wider">
              PASSO {current + 1} DE {steps.length}
            </span>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-background-200/60 flex items-center justify-center text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="min-h-[200px] flex flex-col">
          <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug mb-2">
            {step.label}
          </h3>
          {step.hint && (
            <p className="text-sm text-foreground-500 mb-5">{step.hint}</p>
          )}

          {step.type === "select" ? (
            <select
              value={value}
              onChange={handleChange}
              className={inputBase}
              autoFocus
            >
              <option value="" disabled>
                Selecione uma opção
              </option>
              {step.options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={step.type === "number" ? "text" : step.type}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={step.placeholder}
              className={inputBase}
              autoFocus
            />
          )}

          {/* Navigation */}
          <div className="mt-auto pt-8 flex items-center gap-3">
            {current > 0 && (
              <button
                onClick={handlePrev}
                className="px-5 py-3.5 rounded-xl border border-background-300/50 text-foreground-800 font-semibold text-sm hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Voltar
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canNext}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-primary-500 text-background-50 px-6 py-3.5 text-sm font-semibold tracking-wide transition-all hover:bg-primary-400 hover:shadow-[0_0_30px_-6px_oklch(var(--primary-500)/0.5)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {current === steps.length - 1 ? (
                <>
                  <i className="ri-whatsapp-line"></i>
                  ENVIAR PRO WHATSAPP
                </>
              ) : (
                <>
                  Próximo
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}